import React, {
  createContext,
  useContext,
  useCallback,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cn } from "../utils/helpers";

const ToastContext = createContext(null);

const ICONS = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};

const STYLES = {
  success: "border-accent-green/30 bg-white text-dark-900",
  error: "border-accent-red/30 bg-white text-dark-900",
  info: "border-primary-200 bg-white text-dark-900",
};

const ICON_COLORS = {
  success: "text-accent-green",
  error: "text-accent-red",
  info: "text-primary-500",
};

let idSeq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (type, message) => {
      const id = ++idSeq;
      setToasts((prev) => [...prev, { id, type, message }]);
      setTimeout(() => dismiss(id), 4500);
    },
    [dismiss],
  );

  const toast = {
    success: (m) => push("success", m),
    error: (m) => push("error", m),
    info: (m) => push("info", m),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 w-full max-w-sm">
        <AnimatePresence>
          {toasts.map((t) => {
            const Icon = ICONS[t.type] || Info;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, x: 40, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 40, scale: 0.95 }}
                className={cn(
                  "flex items-start gap-3 rounded-xl border px-4 py-3 shadow-elevated",
                  STYLES[t.type],
                )}
              >
                <Icon className={cn("w-5 h-5 mt-0.5 shrink-0", ICON_COLORS[t.type])} />
                <p className="text-sm font-medium flex-1">{t.message}</p>
                <button
                  onClick={() => dismiss(t.id)}
                  className="text-dark-400 hover:text-dark-700 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  // Tolerate usage outside the provider so review components never crash the
  // page if rendered in isolation; toasts simply become no-ops.
  return ctx || { success: () => {}, error: () => {}, info: () => {} };
}
